import { EncodingResult } from '@mode-vegas/vegas';
import * as angular from 'angular';
import { BehaviorSubject, Observable } from 'rxjs';
import { combineLatest, filter, first, map, startWith, switchMap, takeUntil } from 'rxjs/operators';

import { tokenFromVizID } from '@mode/charts/domain';
import { makeSummaryData, RenderDataModel } from '@mode/charts/util-vegas';
import {
  EmbedActions,
  EmbedActionType,
  EmbedReportActions,
  EmbedReportState,
  ReportStore,
} from '@mode/reports/sandbox/contract';
import { ChartTypes } from '@mode/shared/contract-common';
import { generateUUID, shareReplayDefault, getAppName, isBuilderMode, isReportMode } from '@mode/shared/util-js';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { errorGif } from '@mode/shared/ui-images';
import { AnalyticsEvent, AnalyticsProps, AnalyticsService } from '@mode/shared/util-segment';
import { MixableBaseController } from '../../../common/component';
import { ChartExportHelper } from '../../../lib/chart-export-helper';
import { getChartDataErrorMsg } from '../../../lib/charts/chart-data';
import { ReportApiService } from '../../../services/report-api-service';
import ReportLinkService from '../../../services/report-link-service';
import { EmbedChartSelectors, EmbedReportSelectors, FlamingoSelectors } from '../domain/embed-report.selectors';

class VegasBigValueController extends MixableBaseController {
  static $inject = [
    '$scope',
    '$window',
    'ReportApiService',
    'ReportLinkService',
    'RxStoreService',
    'AnalyticsService',
    'FeatureFlagsFacade',
  ];

  onAction: (args: { $action: EmbedActions }) => void;
  onRenderEnd: (args: { $visualizationRendered: boolean }) => void;
  onRenderStart: (args: { $vizId: string }) => void;

  chartId: string;
  token: string;
  queryToken: string;
  renderInfo: Readonly<ChartTypes.VegasRenderInfo>;
  chartTitle: string;
  description: string;
  reportLevelColorMap: { [seriesName: string]: number };
  dataset: EmbedReportState.EmbedDataset;
  inViewport: boolean;

  isValid$: Observable<boolean>;
  loading$: Observable<boolean>;
  planFailed$: Observable<boolean>;
  planError$: Observable<string>;
  planErrorHelp$: Observable<string>;
  showRenderer$: Observable<boolean>;
  renderModel$: Observable<RenderDataModel>;
  maxFontSize$: Observable<number>;
  queryToken$: Observable<string>;
  noResults$: Observable<boolean>;
  readonly copyText = `Copy to ${getAppName()} clipboard`;
  private shownNotifier = new BehaviorSubject<boolean>(false);
  private shown$: Observable<boolean>;
  unaggregatedExportEnabled$: Observable<boolean>;
  wleCsvExportEnabled$: Observable<boolean>;

  viewer: string;
  isEmbedded: boolean;
  exploreEnabled = false;
  chartMessage: string;
  chartIsValid = true;

  encodingErrors: string[];
  errorImg: string;
  renderError: string;
  renderErrorHelp: string;

  constructor(
    private $scope: angular.IScope,
    private $window: angular.IWindowService,
    private reportApiService: ReportApiService,
    private reportLinkService: ReportLinkService,
    private rxStore: ReportStore,
    private analyticsService: AnalyticsService,
    private featureFlagsFacade: FeatureFlagsFacade
  ) {
    super();
    this.errorImg = errorGif;
    this.shown$ = this.shownNotifier.pipe(
      filter((shown) => shown),
      first() // Don't want to continue firing after being shown once
    );
    this.unaggregatedExportEnabled$ = featureFlagsFacade.asObservable(FeatureFlag.UnaggregatedChartExport);
    this.wleCsvExportEnabled$ = featureFlagsFacade.asObservable(FeatureFlag.WleCsvExport);
  }

  $onInit() {
    this.token = this.chartId.split('_')[1];

    this.queryToken$ = this.rxStore
      .select(EmbedChartSelectors.createQueryTokenSelector(this.token))
      .pipe(shareReplayDefault(), takeUntil(this.destroyed$));

    this.queryToken$
      .pipe(switchMap((queryToken) => this.rxStore.select(EmbedReportSelectors.createDatasetSelector(queryToken))))
      .subscribe((dataset) => {
        this.dataset = dataset;
      });

    this.queryToken$.subscribe((queryToken) => {
      this.queryToken = queryToken;
    });

    this.isValid$ = this.shown$.pipe(
      switchMap(() =>
        this.rxStore.select(FlamingoSelectors.createIsValidSelector(this.token)).pipe(shareReplayDefault())
      )
    );
    this.renderModel$ = this.rxStore
      .select(EmbedChartSelectors.createRenderDataModelSelector(this.token))
      .pipe(shareReplayDefault());
    const flamingoError$ = this.rxStore.select(FlamingoSelectors.createFriendlyErrorSelector(this.token));
    this.planFailed$ = this.rxStore.select(FlamingoSelectors.createPlanFailedSelector(this.token));
    this.planError$ = flamingoError$.pipe(map((err) => err.msg));
    this.planErrorHelp$ = flamingoError$.pipe(map((err) => err.helpText));
    this.loading$ = this.shown$.pipe(
      switchMap(() => this.rxStore.select(FlamingoSelectors.createPlanLoadingSelector(this.token))),
      startWith(true), // Start with true since shown$ can take time to fire
      shareReplayDefault()
    );

    this.noResults$ = this.renderModel$.pipe(map((rm) => rm == null || rm.isEmpty()));

    this.showRenderer$ = this.isValid$.pipe(
      combineLatest(this.planFailed$),
      map(([valid, failed]) => valid && !failed)
    );

    this.rxStore
      .selectNoEmpty(EmbedChartSelectors.createVegasRenderInfoSelector(this.token, true))
      .pipe(takeUntil(this.destroyed$))
      .subscribe((vegasInfo) => {
        this.reportLevelColorMap = vegasInfo.reportLevelColorMap;
      });

    this.maxFontSize$ = this.rxStore.select(EmbedChartSelectors.createBigValueMaxFontSizeSelector(this.token));

    this.loading$
      .pipe(
        takeUntil(this.destroyed$),
        filter((loading) => loading)
      )
      .subscribe(() => this.renderStart());

    this.isValid$
      .pipe(
        combineLatest(this.loading$),
        takeUntil(this.destroyed$),
        filter(([isValid, loading]) => !isValid && !loading)
      )
      .subscribe(([isValid]) => {
        this.chartIsValid = isValid;
        this.renderEnd(null);
      });
  }

  $onChanges(changes: angular.IOnChangesObject) {
    if (changes.renderInfo && this.renderInfo != null) {
      this.chartTitle = this.renderInfo.title;
      this.description = (this.renderInfo.format.bigValue && this.renderInfo.format.bigValue.description) || '';
    }

    if (changes.inViewport && this.inViewport) {
      this.shownNotifier.next(true);
    }
  }

  isLinkable() {
    return this.reportLinkService.isLinkable();
  }

  linkToData($event) {
    if (this.isLinkable()) {
      $event.preventDefault();
      $event.stopPropagation();
      this.reportLinkService.linkToPage(this.queryToken, 'data');
    }
  }

  linkToQuery($event) {
    if (this.isLinkable()) {
      $event.preventDefault();
      $event.stopPropagation();
      this.reportLinkService.linkToPage(this.queryToken, 'query');
    }
  }

  export(title: string) {
    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      location: AnalyticsEvent.SubContext.ReportView,
      action: 'Export chart data to CSV',
      resource: AnalyticsEvent.Context.Report,
      chartType: this.renderInfo.chartType,
    });
    this.renderModel$.pipe(first(), takeUntil(this.destroyed$)).subscribe((rm) => {
      const chartData = makeSummaryData(rm, this.renderInfo.encoding);
      if (chartData) ChartExportHelper.saveAs(ChartExportHelper.makeCsv(chartData), title);
    });
  }

  linkToLightbox($event) {
    this.noResults$.pipe(first()).subscribe((noResults) => {
      if (!noResults && this.isLinkable()) {
        const analyticsProps = this.getAnalyticsProps();

        $event.preventDefault();
        $event.stopPropagation();

        this.onAction({
          $action: {
            type: EmbedActionType.ExploreChart,
            chartToken: tokenFromVizID(this.chartId),
            props: analyticsProps,
          },
        });
      }
    });
  }

  captureKebabClick() {
    const analyticsProps = this.getAnalyticsProps();

    this.onAction({
      $action: {
        type: EmbedActionType.ExpandKebab,
        chartToken: tokenFromVizID(this.chartId),
        props: analyticsProps,
      },
    });
  }

  copySuccess() {
    this.onAction({
      $action: {
        type: EmbedActionType.ShowAlert,
        success: true,
        message: 'Link to chart copied successfully',
      },
    });
  }

  copyChart() {
    this.analyticsService.track(AnalyticsEvent.Name.UiClicked, {
      location: AnalyticsEvent.SubContext.ReportView,
      action: 'Copy Chart',
      resource: AnalyticsEvent.Context.Report,
      id: this.token,
    });
    this.onAction({
      $action: {
        type: EmbedActionType.CopyChart,
        token: this.token,
      },
    });
  }

  isReportMode(): boolean {
    return isReportMode(this.$window.document.body);
  }

  isBuilderMode(): boolean {
    return isBuilderMode(this.$window.document.body);
  }

  updateEncoding(encResult: EncodingResult) {
    this.onAction({
      $action: {
        type: EmbedActionType.UpdateEncoding,
        chartToken: this.chartId.replace('chart_', ''),
        encResult,
      },
    });
  }

  dispatchEmbedAction(action: EmbedActions) {
    this.onAction({ $action: action });
  }

  getAnalyticsProps(): AnalyticsProps {
    return {
      event_id: generateUUID(),
      product_context: AnalyticsEvent.Context.Report,
      sub_context: AnalyticsEvent.SubContext.Chart,
      chart_id: this.chartId,
      chart_type: this.renderInfo.chartType,
    };
  }

  renderStart() {
    this.renderError = null;
    this.renderErrorHelp = null;
    this.onRenderStart({ $vizId: this.chartId });
  }

  renderEnd(err: Error) {
    if (err != null) {
      const { renderError, renderErrorHelp } = getChartDataErrorMsg(err);
      this.renderError = renderError;
      this.renderErrorHelp = renderErrorHelp;
    }
    const visualizationRendered = (err === null || err === undefined) && this.chartIsValid;
    this.onRenderEnd({ $visualizationRendered: visualizationRendered });
  }

  viewUnaggregatedData() {
    this.rxStore.dispatch(new EmbedReportActions.ViewUnaggregatedDataAction(this.token, this.queryToken));
  }
}

export const VegasBigValueComponent: angular.IComponentOptions = {
  bindings: {
    chartId: '@id',
    renderInfo: '<',
    viewer: '<',
    isEmbedded: '<',
    exploreEnabled: '<',
    onRenderStart: '&',
    onRenderEnd: '&',
    onAction: '&',
    inViewport: '<',
  },
  controllerAs: 'ctrl',
  controller: VegasBigValueController,
  template: `
    <div
      class="editor-module editor-chart mode-object editor-multi big-number"
      ng-class="{'has-trend-label': ctrl.trendLabel().length > 0, 'is-phantom-mode': ctrl.isPhantomMode(), 'is-report-mode': ctrl.isReportMode() }">

      <div class="chart-wrapper">

        <div class="chart-content">
          <!-- New report actions start -->
          <div class="new-viz-actions" ng-if="(ctrl.viewer !== 'whiteLabel') && (ctrl.isValid$ | async:this)">
          <copy-link-to-viz link="ctrl.renderInfo._vizReportRunLink">
            <button
                class="new-viz-action hide-2"
                uib-tooltip="Get Share Link"
                copy-to-clipboard
                clipboard-text="ctrl.renderInfo._vizReportRunLink"
                clipboard-success="ctrl.copySuccess()"
                clipboard-enabled="isClipboardEnabled">
                <svg-icon type="'copy-link'"></svg-icon>
              </button>
            </copy-link-to-viz>
            <button
              class="new-viz-action kebab-action underlying-data-menu"
              ng-click="ctrl.captureKebabClick($event)">
              <kebab-menu targetable="true">
                <li menu-clipboard-link class="show-2" link="ctrl.renderInfo._vizReportRunLink" clipboard-enabled="isClipboardEnabled" clipboard-success="ctrl.copySuccess()" ng-show="isClipboardEnabled">Copy chart link</li>
                <li menu-item="exp" role="button" tabindex="0" ng-keyup="$event.keyCode == 13 && ctrl.export(ctrl.chartTitle)" ng-click="ctrl.export(ctrl.chartTitle)" data-test="export-csv-link">Download chart data to CSV</li>
                <li menu-item="exp" class="mode-hide-embed" ng-if="ctrl.unaggregatedExportEnabled$ | async:this && ctrl.viewer !== 'whiteLabel'" ng-click="ctrl.viewUnaggregatedData()">View underlying chart data </li>
                <li menu-item="exp" class="divider"></li>
                <li menu-item="exp" ng-if="!ctrl.viewer" ng-click="ctrl.copyChart()">{{ ctrl.copyText }}</li>
                <li menu-item="exp" class="divider"></li>
                <li menu-item="exp" ng-click="ctrl.linkToData($event)" ng-href="{{ ctrl.dataset.reportDataUrl }}">View raw data <span class="mode-show-embed-inline">in Mode</span></li>
                <li menu-item="exp" ng-click="ctrl.linkToQuery($event)" ng-href="{{ ctrl.dataset.reportQueryUrl }}" ng-if="!ctrl.viewer">View source <span class="mode-show-embed-inline">in Mode</span></li>
              </kebab-menu>
            </button>

            <div
              class="new-viz-actions__section"
              ng-if="!ctrl.viewer && ctrl.exploreEnabled">
              <span class="divider"></span>

              <a class="viz-explore-action btn btn-default"
                ng-class="{ disabled: (ctrl.noResults$ | async:this) }"
                ng-href="{{ ctrl.renderInfo._vizReportRunLink }}"
                ng-click="ctrl.linkToLightbox($event)">
                Explore
              </a>
            </div>
          </div>
          <!-- WLE-specific actions -->

          <div class="new-viz-actions" ng-if="(ctrl.viewer === 'whiteLabel') && (ctrl.isValid$ | async:this) && (ctrl.wleCsvExportEnabled$ | async:this)">
            <button
              class="new-viz-action kebab-action underlying-data-menu"
              ng-click="ctrl.captureKebabClick($event)">
              <kebab-menu targetable="true">
                <li menu-item="exp" role="button" tabindex="0" ng-keyup="$event.keyCode == 13 && ctrl.export(ctrl.chartTitle)" ng-click="ctrl.export(ctrl.chartTitle)" data-test="export-csv-link">Download chart data to CSV</li>
              </kebab-menu>
            </button>
          </div>
          <!-- New report actions end -->

          <div class="chart-big-number" ng-show="(ctrl.showRenderer$ | async:this) && !ctrl.renderError">
            <div class="chart-title clearfix" ng-if="ctrl.chartTitle">
              <in-place-edit
                text="ctrl.chartTitle"
                no-edit="true"
                ng-dblclick="$event.stopPropagation()"></in-place-edit>
            </div>
            <div class="chart-title chart-description clearfix" ng-if="ctrl.description">
              <in-place-edit
                text="ctrl.description"
                no-edit="true"></in-place-edit>
            </div>
            <mode-big-value-renderer
              ng-if="(ctrl.isValid$ | async:this)"
              [token]="ctrl.token"
              [render-model]="ctrl.renderModel$ | async:this"
              [render-context]="'report'"
              [encoding]="ctrl.renderInfo.encoding"
              [format]="ctrl.renderInfo.format"
              [field-formats]="ctrl.renderInfo.fieldFormats"
              (render-start)="ctrl.renderStart()"
              (render-end)="ctrl.renderEnd($err)"
              [max-font-size]="ctrl.maxFontSize$ | async:this">
            </mode-big-value-renderer>
          </div>
          <div class="chart-error" ng-show="(ctrl.planFailed$ | async:this)">
            <h3 ng-bind="ctrl.planError$ | async:this"></h3>
            <p ng-bind="ctrl.planErrorHelp$ | async:this"></p>
          </div>
          <div ng-if="(ctrl.isValid$ | async:this) === false" class="chart-error">
            <h3>Sorry, the chart configuration is invalid or incomplete</h3>
            <p class="sub-message">Try editing the chart to check the configuration</p>
          </div>
          <div class="chart-error" ng-if="ctrl.renderError"><h3 ng-bind="ctrl.renderError" /><p ng-bind="ctrl.renderErrorHelp" /></div>
        </div>
      </div>
    </div>`,
};
